import { VoiceAI } from './client';
import { providers } from './providers';

export interface TTSOptions {
  voice?: string;
  language?: string;
  stream?: boolean;
  format?: 'mp3' | 'wav' | 'aac' | 'flac';
  temperature?: number;
  speed?: number;
  [key: string]: any;
}

export interface TTSResult {
  audio: ArrayBuffer | ReadableStream;
  duration?: number;
  format?: string;
}

async function synthesize(
  text: string,
  model: string,
  options: TTSOptions = {}
): Promise<TTSResult> {
  const client = VoiceAI.getInstance();
  const config = client.getConfig();
  
  const provider = providers.tts[model];
  if (!provider) {
    throw new Error(
      `Model "${model}" not found. Available models: ${Object.keys(providers.tts).join(', ')}\n` +
      `Need a different model? Contact founders at hello@slng.ai`
    );
  }

  return provider.synthesize(text, options, config);
}

export const tts = {
  async synthesize(text: string, model: string, options?: TTSOptions): Promise<TTSResult> {
    return synthesize(text, model, options);
  },
  
  vui: (text: string, options?: TTSOptions) => 
    synthesize(text, 'vui', options),
  
  orpheus: (text: string, options?: TTSOptions) => 
    synthesize(text, 'orpheus', options),
  
  koroko: (text: string, options?: TTSOptions) => 
    synthesize(text, 'koroko', options),
  
  xtts: (text: string, options?: TTSOptions & { speakerVoice: string }) => 
    synthesize(text, 'xtts-v2', options),
  
  mars6: (text: string, options?: TTSOptions & { audioRef: string }) => 
    synthesize(text, 'mars6', options),
  
  orpheusIndic: (text: string, options?: TTSOptions & { language: string }) => {
    if (!options?.language) {
      throw new Error('Orpheus Indic requires language option (hi, ta, te, bn, mr, gu, kn, ml)');
    }
    return synthesize(text, 'orpheus-indic', options);
  },
  
  elevenlabs: {
    multiV2: (text: string, options?: TTSOptions) => 
      synthesize(text, 'elevenlabs/multi-v2', options),
    
    turbo: (text: string, options?: TTSOptions) => 
      synthesize(text, 'elevenlabs/turbo-v2-5', options),
    
    v3: (text: string, options?: TTSOptions) => 
      synthesize(text, 'elevenlabs/v3', options),
    
    flash: (text: string, options?: TTSOptions) => 
      synthesize(text, 'elevenlabs/flash-v2-5', options),
  },

  models: Object.keys(providers.tts),
  
  getVoices: (model: string): string[] => {
    const provider = providers.tts[model];
    return provider?.voices || [];
  },
  
  getLanguages: (model: string): string[] => {
    const provider = providers.tts[model];
    return provider?.languages || ['en'];
  },
};